import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Calendar, FileText } from 'lucide-react';
import RiepilogoQuadratura from './RiepilogoQuadratura';
import transazioniService from '../services/api-transazioni';
import { controllaQuadratura } from '../utils/contabilita';

const TransazioneModal = ({ isOpen, onClose, transazione, onSave, mode = 'create', contiList = [] }) => {
  const [formData, setFormData] = useState({
    id: null,
    descrizione: '',
    data_transazione: new Date().toISOString().split('T')[0],
    dettagli: []
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [originalDettagli, setOriginalDettagli] = useState([]); // Track original values for balance calculation

  useEffect(() => {
    if (transazione && mode === 'edit') {
      // Fully repopulate all rows with both accounts and amounts
      const dettagliPopolati = (transazione.dettagli || []).map(d => ({
        ...d,
        importo_text: d.importo?.toString() || '0'
      }));
      
      setFormData({
        id: transazione.id,
        descrizione: transazione.descrizione || '',
        data_transazione: transazione.data_transazione?.split('T')[0] || new Date().toISOString().split('T')[0],
        dettagli: dettagliPopolati
      });
      
      // Store original values for balance calculation
      setOriginalDettagli(JSON.parse(JSON.stringify(dettagliPopolati)));
    } else {
      setFormData({
        id: null,
        descrizione: '',
        data_transazione: new Date().toISOString().split('T')[0],
        dettagli: [{
          id: Date.now(),
          descrizione_dettaglio: '',
          importo: 0,
          importo_text: '',
          conto_fk: null
        }]
      });
      setOriginalDettagli([]);
    }
    setError('');
  }, [transazione, mode, isOpen]);

  const addRigaDettaglio = () => {
    setFormData(prev => ({
      ...prev,
      dettagli: [...prev.dettagli, {
        id: Date.now(),
        descrizione_dettaglio: '',
        importo: 0,
        importo_text: '',
        conto_fk: null
      }]
    }));
  };

  const insertRigaAtPosition = (index) => {
    setFormData(prev => ({
      ...prev,
      dettagli: [
        ...prev.dettagli.slice(0, index + 1),
        {
          id: Date.now(),
          descrizione_dettaglio: '',
          importo: 0,
          importo_text: '',
          conto_fk: null
        },
        ...prev.dettagli.slice(index + 1)
      ]
    }));
  };

  const updateRigaDettaglio = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      dettagli: prev.dettagli.map((riga, i) => 
        i === index ? { ...riga, [field]: value } : riga
      )
    }));
  };

  const handleContoChange = (index, newContoFk) => {
    if (mode === 'edit') {
      const oldRiga = formData.dettagli[index];
      const oldContoFk = oldRiga.conto_fk;
      const importo = parseFloat(oldRiga.importo_text) || 0;
      
      // In edit mode: subtract from old account, add to new account
      // This is tracked via originalDettagli for balance calculation
      updateRigaDettaglio(index, 'conto_fk', newContoFk);
    } else {
      updateRigaDettaglio(index, 'conto_fk', newContoFk);
    }
  };

  const handleImportoBlur = (index) => {
    if (mode === 'edit') {
      const riga = formData.dettagli[index];
      const originalRiga = originalDettagli[index];
      
      if (originalRiga) {
        const oldImporto = parseFloat(originalRiga.importo) || 0;
        const newImporto = parseFloat(riga.importo_text) || 0;
        
        // Update original to reflect the change for balance calculation
        setOriginalDettagli(prev => 
          prev.map((r, i) => i === index ? { ...r, importo: newImporto } : r)
        );
      }
    }
  };

  const handleContoBlur = (index) => {
    if (mode === 'edit') {
      const riga = formData.dettagli[index];
      const originalRiga = originalDettagli[index];
      
      if (originalRiga) {
        // Update original to reflect the account change for balance calculation
        setOriginalDettagli(prev => 
          prev.map((r, i) => i === index ? { ...r, conto_fk: riga.conto_fk } : r)
        );
      }
    }
  };

  const removeRigaDettaglio = (index) => {
    setFormData(prev => ({
      ...prev,
      dettagli: prev.dettagli.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Controllo quadratura
    const dettagliValidi = formData.dettagli
      .filter(d => d.descrizione_dettaglio && d.conto_fk && d.importo !== 0)
      .map(d => ({
        ...d,
        // Trasforma il testo in numero per il salvataggio
        importo: parseFloat(d.importo_text || d.importo.toString()) || 0
      }));
    if (dettagliValidi.length === 0) {
      setError('Inserisci almeno una riga di dettaglio valida');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const transazioneData = {
        ...formData,
        dettagli: dettagliValidi,
        user_id: 'user001' // ID corretto dell'utente mpolleschi
      };

      console.log('🚀 Salvataggio transazione:', transazioneData);
      
      if (mode === 'create') {
        console.log('📝 Creazione nuova transazione...');
        await transazioniService.createTransazione(transazioneData);
        console.log('✅ Transazione creata con successo');
      } else if (mode === 'edit' && formData.id) {
        console.log('✏️ Aggiornamento transazione ID:', formData.id);
        await transazioniService.updateTransazione(formData.id, transazioneData);
        console.log('✅ Transazione aggiornata con successo');
      }
      
      onSave();
      onClose();
    } catch (err) {
      console.error('❌ Errore salvataggio transazione:', err);
      setError(err.message || 'Errore salvataggio transazione');
    } finally {
      setLoading(false);
    }
  };

  const controlloQuadratura = () => {
    const dettagliValidi = formData.dettagli.filter(d => d.descrizione_dettaglio && d.conto_fk && d.importo !== 0);
    return dettagliValidi;
  };

  const isTransazioneQuadrata = () => {
    const dettagliValidi = formData.dettagli.filter(d => d.descrizione_dettaglio && d.conto_fk && d.importo !== 0);
    const controllo = controllaQuadratura(dettagliValidi);
    return controllo.isQuadrato;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {mode === 'create' ? 'Nuova Transazione' : 'Modifica Transazione'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Dati Generali */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrizione *
              </label>
              <input
                type="text"
                value={formData.descrizione}
                onChange={(e) => setFormData(prev => ({ ...prev, descrizione: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                placeholder="Es: Acquisto computer"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Transazione *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={formData.data_transazione}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_transazione: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  required
                />
              </div>
            </div>
          </div>

          {/* Dettagli Transazione */}
          <div>
            <div className="flex items-center mb-4">
              <h4 className="text-md font-medium text-gray-900">Dettagli Transazione</h4>
            </div>

            <div className="space-y-2">
              {formData.dettagli.map((riga, index) => (
                <div key={riga.id} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => insertRigaAtPosition(index)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Inserisci riga dopo questa"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  
                  <input
                    type="text"
                    value={riga.descrizione_dettaglio}
                    onChange={(e) => updateRigaDettaglio(index, 'descrizione_dettaglio', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-sm"
                    placeholder="Descrizione dettaglio"
                  />
                  
                  <select
                    value={riga.conto_fk || ''}
                    onChange={(e) => handleContoChange(index, e.target.value)}
                    onBlur={() => handleContoBlur(index)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-sm"
                  >
                    <option value="">Seleziona conto</option>
                    {contiList
                      .filter(c => c.hasTransactions) // Solo conti transazionali
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(conto => (
                        <option key={conto.id} value={conto.id}>
                          {conto.name}
                        </option>
                      ))}
                  </select>

                  <div className="flex items-center space-x-1">
                    <input
                      type="text"
                      value={riga.importo_text || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        
                        // Salva come testo puro
                        updateRigaDettaglio(index, 'importo_text', value);
                        
                        // Calcola valore numerico per il badge
                        const numericValue = parseFloat(value) || 0;
                        updateRigaDettaglio(index, 'importo', numericValue);
                      }}
                      onBlur={() => handleImportoBlur(index)}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-sm"
                      placeholder="0.00"
                    />
                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                      (riga.importo || 0) >= 0 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {(riga.importo || 0) >= 0 ? 'DARE' : 'AVERE'}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeRigaDettaglio(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Elimina riga"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Riepilogo Quadratura */}
          <RiepilogoQuadratura dettagli={controlloQuadratura()} />

          {/* Pulsanti Azione */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || !controlloQuadratura().length || !controlloQuadratura().every(d => d.descrizione_dettaglio && d.conto_fk && d.importo !== 0) || !isTransazioneQuadrata()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              <span>{mode === 'create' ? 'Crea Transazione' : 'Salva Modifiche'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransazioneModal;
